// Pure decision for the dashboard seller-onboarding gate. Kept free of React
// and network so it can be unit-tested (SellerOnboardingGate.test.js).

export const LOCKED_ALLOWED_PREFIXES = [
  "/apply",
  "/profile",
  "/logout",
  "/settings/billing",
];

const pathAllowedWhileLocked = (pathname = "") =>
  LOCKED_ALLOWED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

const NONE = { redirect: null, banner: null };

// Statuses that need the seller's attention even when nothing is held yet.
const ACTION_STATUSES = ["needs_info", "rejected", "suspended"];

/**
 * @param {object} args
 * @param {object|null} args.me          GET /seller-onboarding/me response
 * @param {string}      args.pathname    current location.pathname
 * @param {boolean}     args.isSeller    isSellerAccountUser(currentUser)
 * @param {boolean}     args.impersonating isImpersonating(getMockUser())
 * @returns {{ redirect: string|null, banner: null|{ kind: "continue"|"needs_info"|"pending"|"closed"|"grace", status: string } }}
 *
 * Orders are only held (and locked mode only locks) when `me.holdsNow` is
 * true, or the application needs action (needs_info / rejected / suspended).
 * Existing sellers inside the printer's grace window (`me.inGrace`) get a
 * soft reminder and are never redirected.
 */
export function onboardingGateDecision({ me, pathname, isSeller, impersonating }) {
  if (!isSeller || impersonating || !me) return NONE;
  if (!me.enabled || !me.required) return NONE;

  const status = me.application?.status || "not_started";
  if (status === "approved") return NONE;

  const onApply = pathname === "/apply" || String(pathname || "").startsWith("/apply/");
  const holding = me.holdsNow === true || ACTION_STATUSES.includes(status);

  if (!holding) {
    if (onApply) return NONE;
    if (status === "pending_review") {
      return { redirect: null, banner: { kind: "pending", status } };
    }
    if (me.inGrace) return { redirect: null, banner: { kind: "grace", status } };
    return NONE;
  }

  const locked = me.accessMode === "locked";
  if (locked && status !== "pending_review" && !pathAllowedWhileLocked(pathname)) {
    return { redirect: "/apply", banner: null };
  }

  // The wizard has its own status screens — no banner on /apply itself.
  if (onApply) return NONE;

  let kind;
  if (status === "pending_review") kind = "pending";
  else if (status === "rejected" || status === "suspended") kind = "closed";
  else if (status === "needs_info") kind = "needs_info";
  else kind = "continue";

  return { redirect: null, banner: { kind, status } };
}

export default onboardingGateDecision;
